// Saved per-answer marks are the source of truth. Reading a result never re-runs AI.
const finiteNumber = value => {
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    if (typeof value === 'string' && !value.trim()) return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};
const roundMark = value => Math.round((value + Number.EPSILON) * 100) / 100;
const nonnegative = value => roundMark(Math.max(0, finiteNumber(value) ?? 0));
const boundMark = (value, maximum) => roundMark(Math.max(0, maximum === null ? value : Math.min(value, maximum)));
const uniqueMark = values => {
    const unique = [...new Set(values)];
    return unique.length === 1 ? unique[0] : null;
};

// Do not take the last fraction in feedback: it may be a rubric item or advice.
// An ambiguous response stays unmarked so it can be reviewed, rather than earning a fabricated zero.
export function parseAIMark(text, maxMarks, { finalOnly = false } = {}) {
    if (typeof text !== 'string') return null;
    const clean = text.replace(/<[^>]*>/g, ' ').replace(/[*_`#]/g, '').replace(/[⁄／]/g, '/');
    const maximum = finiteNumber(maxMarks);
    const number = '(-?\\d+(?:\\.\\d+)?)';
    const ratio = `(?:\\s*(?:/|out\\s+of)\\s*${number})?`;
    const groups = [
        '(?:final(?:\\s+suggested)?|overall)\\s+(?:marks?(?:\\s+awarded)?|score)',
        'total\\s+(?:marks?\\s+awarded|score)|total\\s+marks?(?!\\s+(?:available|possible|for))',
        '(?:suggested\\s+marks?|marks?\\s+awarded|score)',
    ];
    for (const label of groups.slice(0, finalOnly ? 2 : groups.length)) {
        const re = new RegExp(`\\b(?:${label})\\s*(?::|=|is|of)?\\s*${number}${ratio}`, 'gi');
        const candidates = [];
        let foundLabel = false;
        for (const match of clean.matchAll(re)) {
            foundLabel = true;
            const mark = Number(match[1]);
            const denominator = match[2] === undefined ? null : Number(match[2]);
            if (denominator !== null && (denominator <= 0 || (maximum !== null && denominator !== maximum))) continue;
            // A bare "total marks" value states a maximum as often as an award.
            if (/^total\s+marks?\b/i.test(match[0]) && !/^total\s+marks?\s+awarded\b/i.test(match[0]) && denominator === null) continue;
            candidates.push(boundMark(mark, maximum ?? denominator));
        }
        if (foundLabel) return uniqueMark(candidates);
    }
    if (finalOnly) return null;
    const candidates = [];
    const re = new RegExp(`${number}\\s*(?:/|out\\s+of)\\s*${number}`, 'gi');
    for (const match of clean.matchAll(re)) {
        const denominator = Number(match[2]);
        if (denominator <= 0 || (maximum !== null && denominator !== maximum)) continue;
        candidates.push(boundMark(Number(match[1]), maximum ?? denominator));
    }
    return uniqueMark(candidates);
}

export function createAIEvaluation(response, maxMarks) {
    const text = typeof response?.evaluationText === 'string' ? response.evaluationText : '';
    const maximum = finiteNumber(maxMarks);
    const structuredMark = finiteNumber(response?.mark);
    const mark = structuredMark === null ? parseAIMark(text, maximum) : boundMark(structuredMark, maximum);
    return {
        text: text || 'No evaluation feedback was returned.',
        mark, maxMarks: nonnegative(maxMarks), method: 'ai', markParserVersion: 1,
        status: mark === null ? 'needs-review' : 'graded',
    };
}

export function getEvaluationMark(evaluation) {
    if (!evaluation || typeof evaluation !== 'object') return null;
    if (['failed', 'pending', 'processing', 'marking'].includes(evaluation.status)) return null;
    // Recover an explicit final award in saved feedback from older prose-only AI
    // responses. Ambiguous or incomplete feedback still requires a retry.
    if (evaluation.status === 'needs-review') return parseAIMark(evaluation.text, evaluation.maxMarks, { finalOnly: true });
    // Older AI responses used the last fraction. Recover only an explicitly labelled
    // final award, without asking AI to grade again or changing the saved feedback.
    if (!evaluation.method && !evaluation.markParserVersion) {
        const explicit = parseAIMark(evaluation.text, evaluation.maxMarks, { finalOnly: true });
        if (explicit !== null) return explicit;
    }
    const stored = finiteNumber(evaluation.mark);
    if (stored !== null) return boundMark(stored, finiteNumber(evaluation.maxMarks));
    return parseAIMark(evaluation.text, evaluation.maxMarks);
}

export function getQuestionEntry(entries, index, subIndex = null, questionId) {
    if (!entries) return undefined;
    if (subIndex !== null) return entries[index]?.[subIndex] ?? entries[`${index}-${subIndex}`] ?? entries[questionId];
    return entries[index] ?? entries[questionId];
}

export function flattenExamQuestions(questions = []) {
    return (Array.isArray(questions) ? questions : []).flatMap((question, index) => {
        if (!question || typeof question !== 'object') return [];
        if (question.type === 'case-study') return (Array.isArray(question.subQuestions) ? question.subQuestions : []).flatMap((child, subIndex) => child && typeof child === 'object' ? [{
            question: child, index, subIndex, key: `${index}-${subIndex}`,
        }] : []);
        // Also accept the flattened snapshots used by older analytics exports.
        if (question.isCaseStudyChild) return [{ question, index: question.originalIndex, subIndex: question.subIndex, key: `${question.originalIndex}-${question.subIndex}` }];
        return [{ question, index, subIndex: null, key: String(index) }];
    });
}

// questions must be the saved attempt's version, not a newer exam draft. Pending
// totals are confirmed subtotals, never a final grade. For old aggregate-only
// results we retain the saved numeric total unless there is evidence it is pending.
export function getAttemptScores(attempt = {}, questions = attempt?.examQuestions) {
    attempt = attempt || {};
    const evaluations = attempt.evaluations || {};
    let entries = new Map();
    const isEvaluation = value => value && typeof value === 'object' && ('mark' in value || 'text' in value || 'status' in value);
    // Nested entries take priority over legacy flat duplicates, matching the answer review.
    Object.entries(evaluations).forEach(([parent, value]) => {
        if (value && typeof value === 'object' && !isEvaluation(value)) {
            Object.entries(value).forEach(([child, evaluation]) => {
                if (isEvaluation(evaluation)) entries.set(`${parent}-${child}`, evaluation);
            });
        }
    });
    Object.entries(evaluations).forEach(([key, value]) => {
        if (isEvaluation(value) && !entries.has(key)) entries.set(key, value);
    });
    const declaredPending = ['pending', 'processing', 'marking', 'failed', 'needs-review'].includes(attempt.evaluationStatus);
    const expected = flattenExamQuestions(questions).filter(entry => entry.question.type === 'descriptive');
    const legacyAggregate = Object.keys(evaluations).length === 0 && !declaredPending && finiteNumber(attempt.aiGradedScore) !== null;
    const missingKeys = [];
    if (expected.length && !legacyAggregate) {
        // Canonicalize the same way as answer review: numeric/nested entries win
        // over ID aliases, so duplicated legacy keys cannot award marks twice.
        entries = new Map();
        expected.forEach(entry => {
            const evaluation = getQuestionEntry(evaluations, entry.index, entry.subIndex, entry.question.id);
            if (isEvaluation(evaluation)) entries.set(entry.key, evaluation);
            else missingKeys.push(entry.key);
        });
    }
    let cents = 0;
    const pendingKeys = [...missingKeys];
    [...entries.keys()].sort().forEach(key => {
        const mark = getEvaluationMark(entries.get(key));
        if (mark === null) pendingKeys.push(key);
        else cents += Math.round(mark * 100);
    });
    // A persisted pending flag protects omitted legacy failures. A complete set
    // of confirmed marks for the frozen question version is stronger evidence:
    // older manual-marking clients may not have updated the aggregate status.
    const allExpectedConfirmed = expected.length > 0 && entries.size === expected.length && pendingKeys.length === 0;
    const unresolvedStatus = declaredPending && !allExpectedConfirmed;
    const pendingCount = Math.max(pendingKeys.length, unresolvedStatus ? 1 : 0);
    const pendingCountExact = !(unresolvedStatus && (pendingKeys.length === 0 || expected.length === 0));
    const aiScore = legacyAggregate ? nonnegative(attempt.aiGradedScore) : cents / 100;
    const autoScore = nonnegative(attempt.score);
    const autoTotal = nonnegative(attempt.autoGradedTotalMarks);
    const aiTotal = nonnegative(attempt.descriptiveTotalMarks);
    return {
        autoScore, autoTotal, aiScore, aiTotal,
        totalScore: roundMark(autoScore + aiScore),
        totalPossible: roundMark(autoTotal + aiTotal),
        pendingCount, hasPending: pendingCount > 0, pendingKeys, pendingCountExact,
    };
}
